import { logger } from './logger';
import { apiFetch } from './api';
import type { ClioContact } from '../types';

export interface CreateContactPayload {
  name: string;
  type: string;
  first_name?: string;
  middle_name?: string;
  last_name?: string;
  phone?: string;
  email?: string;
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  company_id?: string;
}

// Merge contact snapshots by Clio id while preserving the order of the first
// list. Later lists win, so a matter-specific/search result can refresh the
// firm-wide snapshot without duplicating the contact.
export function mergeClioContacts(...contactLists: readonly ClioContact[][]): ClioContact[] {
  const contactsById = new Map<string, ClioContact>();
  contactLists.forEach(list => list.forEach(contact => contactsById.set(contact.id, contact)));
  return Array.from(contactsById.values());
}

export function upsertClioContact(contacts: ClioContact[], contact: ClioContact): ClioContact[] {
  return mergeClioContacts(contacts, [contact]);
}

// Prefer the matter-related shortlist, but never lose the names for contacts
// already linked in the form. If Clio's shortlist request fails entirely, the
// live directory is a safe fallback instead of rendering empty pickers.
export function resolveMatterContactOptions(
  matterContacts: ClioContact[],
  liveContacts: ClioContact[],
  linkedContactIds: readonly string[],
): ClioContact[] {
  if (matterContacts.length === 0) return liveContacts;
  const linkedIds = new Set(linkedContactIds.filter(Boolean));
  return mergeClioContacts(matterContacts, liveContacts.filter(contact => linkedIds.has(contact.id)));
}

// POSTs a new Clio contact. Extracted from two near-identical blocks in
// App.tsx's handleSaveParty (one for the party's primary contact, one for
// counsel) that built this same payload/request/response shape.
export async function createContact(payload: CreateContactPayload): Promise<ClioContact | null> {
  try {
    const res = await apiFetch(`/api/v1/contacts`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) return null;
    return await res.json();
  } catch (err) {
    logger.error('Failed to create contact:', err);
    return null;
  }
}
